import { IonApp, IonRouterOutlet } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { observer } from 'mobx-react';
import { FC, PropsWithChildren, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import { AnvilProvider, Flex, Toaster } from '@servicetitan/anvil2';
import { ErrorBoundary } from '@servicetitan/error-boundary';
import { LogService } from '@servicetitan/log-service';
import { Provider, useDependencies } from '@servicetitan/react-ioc';
import { App, AppLoadingState } from '@servicetitan/web-feature-app-service';
import { Spinner, useViewportFitCover } from '@servicetitan/web-feature-ui';

import {
    createAxiosInstanceWithJWT,
    MOBILE_WEBVIEW_AXIOS_INSTANCE,
    MOBILE_WEBVIEW_BFF_BASE_URL_TOKEN,
    MobileMaintenanceServiceEntryApi,
    MobileContextApi,
    MobileVehicleApi,
} from '@fleet-pro/mobile-webview-api';

import { OnboardingRequiredPage } from './modules/common/components/onboarding-required-page';
import { UnavailablePage } from './modules/common/components/unavailable-page';
import { FeatureSetsDataStore } from './modules/common/features/feature-set';
import { useAxiosInterceptors } from './modules/common/hooks/use-axios-interceptors';
import { UserStatusDataStore } from './modules/common/stores/user-status-data.store';
import { getMobileGatewayUrl } from './modules/common/utils/get-mobile-gateway-url';
import { themeLight } from './modules/common/utils/light';
import { RequestState } from './modules/common/utils/request-states';
import { CreateServiceEntryPage } from './modules/service-entries/components/create-service-entry';
import { PreviewServiceEntryPage } from './modules/service-entries/components/preview-service-entry';
import { ServiceEntriesPage } from './modules/service-entries/components/service-entries';

const microServiceUrl = getMobileGatewayUrl(globalThis.location.origin);

export const WebApp = () => {
    return (
        <Providers>
            <IonReactRouter>
                <IonRouterOutlet>
                    <AppRoutes />
                </IonRouterOutlet>
            </IonReactRouter>
        </Providers>
    );
};

const AppRoutes = observer(() => {
    const [
        { fetchUserStatus, cancelFetchUserStatus, userStatusRequestState, isOnboardingComplete },
        { fetchFeatureSets, cancelFetchFeatureSets, featureSetsRequestState, availableFeatures },
    ] = useDependencies(UserStatusDataStore, FeatureSetsDataStore);
    const history = useHistory();

    const userStatusLoading =
        userStatusRequestState === RequestState.Idle ||
        userStatusRequestState === RequestState.Loading;

    const featureSetsLoading =
        featureSetsRequestState === RequestState.Idle ||
        featureSetsRequestState === RequestState.Loading;

    const isLoading = userStatusLoading || featureSetsLoading;

    useViewportFitCover();
    useAxiosInterceptors();

    // Fetch user status and feature sets on mount
    useEffect(() => {
        fetchUserStatus();
        fetchFeatureSets();

        return () => {
            cancelFetchUserStatus();
            cancelFetchFeatureSets();
        };
    }, [fetchUserStatus, cancelFetchUserStatus, fetchFeatureSets, cancelFetchFeatureSets]);

    // Redirect based on status after loading
    useEffect(() => {
        if (isLoading) {
            return;
        }

        // First check onboarding status
        if (!isOnboardingComplete) {
            history.replace('/onboarding-required');
            return;
        }

        // Then check available features
        if (!availableFeatures.size) {
            history.replace('/unavailable');
        }
    }, [isLoading, isOnboardingComplete, availableFeatures.size, history]);

    // Signal app loading finished after both are loaded
    useEffect(() => {
        if (!isLoading) {
            App.loading({ state: AppLoadingState.Finished });
        }
    }, [isLoading]);

    // Show spinner while loading
    if (isLoading) {
        return (
            <Flex justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
                <Spinner size="3.75rem" />
            </Flex>
        );
    }

    return (
        <Switch>
            <Route exact path="/onboarding-required" component={OnboardingRequiredPage} />
            <Route exact path="/unavailable" component={UnavailablePage} />
            <Route exact path="/service-entries" component={ServiceEntriesPage} />
            <Route exact path="/service-entries/new" component={CreateServiceEntryPage} />
            <Route
                exact
                path="/service-entries/:serviceEntryId/edit"
                component={CreateServiceEntryPage}
            />
            <Route
                exact
                path="/service-entries/:serviceEntryId"
                component={PreviewServiceEntryPage}
            />
            <Route render={() => <Redirect to="/service-entries" />} />
        </Switch>
    );
});

const Providers: FC<PropsWithChildren> = ({ children }) => {
    return (
        <LogService endpoint={`${microServiceUrl}/api/v2/Log/send-st-core`}>
            <ErrorBoundary moduleName="FleetProWebview">
                <Provider
                    singletons={[
                        MobileContextApi,
                        UserStatusDataStore,
                        FeatureSetsDataStore,
                        MobileMaintenanceServiceEntryApi,
                        MobileVehicleApi,
                        {
                            provide: MOBILE_WEBVIEW_BFF_BASE_URL_TOKEN,
                            useValue: microServiceUrl,
                        },
                        {
                            provide: MOBILE_WEBVIEW_AXIOS_INSTANCE,
                            useValue: createAxiosInstanceWithJWT(),
                        },
                    ]}
                >
                    <IonApp>
                        <Toaster id="fleet-pro-webview-toaster" />
                        <AnvilProvider
                            themeData={{ theme: themeLight, mode: 'light' }}
                            trackingIdData={{
                                optOut: true,
                            }}
                        >
                            {children}
                        </AnvilProvider>
                    </IonApp>
                </Provider>
            </ErrorBoundary>
        </LogService>
    );
};
